import React, { Component } from "react";
import { Button, Input, Form, Col, Row, Icon, Steps, notification, message, Switch } from "antd";
import { withRouter } from "react-router-dom";
import api from "../../../services/api";
import "./index.css";
import * as texts from "../locales";
import PropTypes from "prop-types";
import { FreireContext } from "../../../utils/freireContext";
import {
  applyAllowancesChange,
  DEFAULT_ALLOWANCES,
  buildVisibilityGroupSubmissionPayload,
} from "../constants";
import VisibilityGroupAllowancesSection from "../components/VisibilityGroupAllowancesSection";

const { Step } = Steps;

class NewVisibilityGroup extends Component {
  static contextType = FreireContext;

  static propTypes = {
    form: PropTypes.shape({
      getFieldValue: PropTypes.func,
      setFieldsValue: PropTypes.func,
    }),
  };

  state = {
    loadingSave: false,
    loadingStations: false,
    stations: [],
    loadingCompanies: false,
    companies: [],
    selectedAllowances: [...DEFAULT_ALLOWANCES],
  };

  componentDidMount() {
    this.getCompanies();
  }

  getCompanies() {
    const { freire } = this.context;

    this.setState({ loadingCompanies: true }, async () => {
      try {
        const { data } = await api.get(`/company`);
        this.setState({
          loadingCompanies: false,
          companies: data,
        });
      } catch (error) {
        message.error(freire(texts.ERROR_GET_COMPANIES));
        this.setState({ loadingCompanies: false });
      }
    });
  }

  getStations() {
    const { freire } = this.context;

    this.setState({ loadingStations: true, stations: [] }, async () => {
      try {
        const { data } = await api.get(`select/connected_stations`);
        this.setState({
          loadingStations: false,
          stations: data,
        });
      } catch (error) {
        message.error(freire(texts.ERROR_GET_STATIONS));
        this.setState({
          loadingStations: false,
        });
      }
    });
  }

  handleAllowancesChange = (values = []) => {
    const normalized = applyAllowancesChange(this.props.form, values);
    this.setState({ selectedAllowances: normalized });
  };

  save = () => {
    const { freire } = this.context;

    this.props.form.validateFields(async (err, payload) => {
      if (err) {
        return;
      }

      try {
        this.setState({ loadingSave: true });

        const { payload: submissionPayload } =
          buildVisibilityGroupSubmissionPayload(this.props.form, payload);

        await api.post("visibilityGroup", submissionPayload);

        notification.success({
          message: freire(texts.ALL_RIGHT),
          description: freire(texts.VISIBILITY_CREATED_SUCCESS),
        });
        this.setState({ loadingSave: false });

        this.props.history.push("/dashboard/visibility_group");
      } catch (error) {
        this.setState({ loadingSave: false });

        const {
          response: {
            data: { message },
          },
        } = error;

        notification.error({
          message: freire(texts.WENT_WONG),
          description: message || error.message,
        });
      }
    });
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    const {
      loadingSave,
      loadingStations,
      stations,
      loadingCompanies,
      companies,
      selectedAllowances,
    } = this.state;
    const { freire } = this.context;

    return (
      <div className="container">
        <div className="content">
          <Row gutter={[16, 0]}>
            <Col span={6}>
              <Steps current={1} size="small" direction="vertical">
                <Step
                  icon={<Icon type="profile" />}
                  title={freire(texts.NEW_VISIBILITY_GROUP)}
                  description={freire(texts.CREATE_VISIBILITY_GROUP)}
                />
              </Steps>
            </Col>

            <Col span={18}>
              <Form wrapperCol={{ span: 24 }}>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item label={freire(texts.GROUP_NAME)}>
                      {getFieldDecorator("name", {
                        rules: [
                          {
                            required: true,
                            message: freire(texts.REQUIRED_FIELD),
                          },
                        ],
                      })(
                        <Input
                          disabled={loadingSave}
                          placeholder={freire(texts.GROUP_NAME_EX)}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={freire(texts.EMAIL_DOMAIN)}>
                      {getFieldDecorator("email")(
                        <Input
                          addonBefore="@"
                          disabled={loadingSave}
                          placeholder={freire(texts.EMAIL_EX)}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <VisibilityGroupAllowancesSection
                  form={this.props.form}
                  freire={freire}
                  loading={loadingSave}
                  loadingStations={loadingStations}
                  stations={stations}
                  onAllowancesChange={this.handleAllowancesChange}
                  onStationsDropdownVisibleChange={(opened) => {
                    if (opened && stations.length === 0) {
                      this.getStations();
                    }
                  }}
                  companies={companies}
                  loadingCompanies={loadingCompanies}
                  selectedAllowances={selectedAllowances}
                />

                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item label={freire(texts.VISIBLE_TO_EVERYONE)}>
                      {getFieldDecorator("visibleToEveryone", {
                        valuePropName: "checked",
                        initialValue: false,
                      })(
                        <Switch
                          disabled={loadingSave}
                          checkedChildren={freire(texts.YES)}
                          unCheckedChildren={freire(texts.NO)}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item label={freire(texts.ADD_USER_BY_QRCODE)}>
                      {getFieldDecorator("addOnQr", {
                        valuePropName: "checked",
                        initialValue: false,
                      })(
                        <Switch
                          disabled={loadingSave || this.props.form.getFieldValue("allowExternalCharge") === false}
                          checkedChildren={freire(texts.YES)}
                          unCheckedChildren={freire(texts.NO)}
                          onChange={(checked) => {
                            if (checked) {
                              this.props.form.setFieldsValue({ allowExternalCharge: true });
                            }
                          }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col span={24}>
                    <Form.Item
                      label={freire(texts.ALLOW_EXTERNAL_CHARGE)}
                      extra={freire(texts.ALLOW_EXTERNAL_CHARGE_HELP)}
                    >
                      {getFieldDecorator("allowExternalCharge", {
                        valuePropName: "checked",
                        initialValue: true,
                      })(
                        <Switch
                          disabled={loadingSave || this.props.form.getFieldValue("addOnQr") === true}
                          checkedChildren={freire(texts.YES)}
                          unCheckedChildren={freire(texts.NO)}
                          onChange={(checked) => {
                            if (!checked) {
                              this.props.form.setFieldsValue({ addOnQr: false });
                            }
                          }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Form>

              <div className="footer">
                <Button
                  style={{ marginRight: 20 }}
                  onClick={() =>
                    this.props.history.push("/dashboard/visibility_group")
                  }
                  type="default"
                  disabled={loadingSave}
                >
                  {freire(texts.CANCEL)}
                </Button>
                <Button
                  loading={loadingSave}
                  onClick={this.save}
                  type="primary"
                  disabled={loadingSave}
                >
                  {freire(texts.SAVE)}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

const PageNewVisibilityGroup = Form.create({ name: "inputs" })(
  NewVisibilityGroup
);
export default withRouter(PageNewVisibilityGroup);
